import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Send, X, Zap, ChevronDown, Circle, User, ClipboardCheck, ClipboardList, Droplet, Waves, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import './workflow-premium.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * MLS-WF-UNIFY-001 — reusable, config-driven Workflow Actions panel (MLS). Product wiring
 * (endpoints, guards, active state) is passed in; nothing product-specific lives here.
 * MLS-WF-PREMIUM-001 — premium visual pass: soft-tinted action cards (orange / purple / blue),
 * animated expansion, white active-state card with accent bar, premium chips, completion checklist.
 *
 *  cards        [{ value, label, description, Icon, disabled, disabledReason }]
 *  selected / onSelect / panels / activeState / banner / footer — unchanged contract
 */
const TONES = {
  pending: { tint: 'bg-orange-50/80 hover:bg-orange-50', ring: 'ring-2 ring-orange-300 bg-orange-50', icon: 'text-orange-500', badge: 'bg-orange-500', swoosh: 'is-orange' },
  clarification: { tint: 'bg-purple-50/80 hover:bg-purple-50', ring: 'ring-2 ring-purple-400 bg-purple-50', icon: 'text-purple-600', badge: 'bg-purple-500', swoosh: '' },
  mailaway: { tint: 'bg-blue-50/80 hover:bg-blue-50', ring: 'ring-2 ring-blue-300 bg-blue-50', icon: 'text-blue-600', badge: 'bg-blue-500', swoosh: 'is-blue' },
};
const toneOf = (value) => TONES[value] || TONES.clarification;

const ActionCard = ({ card, isSelected, onSelect }) => {
  const t = toneOf(card.value);
  const { Icon } = card;
  return (
    <button type="button" role="radio" aria-checked={isSelected} disabled={!!card.disabled}
      title={card.disabled && card.disabledReason ? card.disabledReason : undefined}
      onClick={() => onSelect(card.value)}
      className={`wf-card relative w-full text-left rounded-2xl px-4 py-3.5 flex items-center gap-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400
        ${isSelected ? `is-selected ${t.ring}` : t.tint} ${card.disabled ? 'is-disabled opacity-50 cursor-not-allowed' : ''}`}
      data-testid={`status-option-${card.value}`}>
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ${t.icon}`}>
        {Icon && <Icon className="h-5 w-5" />}
      </span>
      <span className="min-w-0">
        <span className="block text-[15px] font-semibold text-slate-900">{card.label}</span>
        <span className="block text-[12.5px] text-slate-500 truncate">{card.description}</span>
      </span>
      {isSelected && (
        <span className={`absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full ${t.badge} ring-2 ring-white flex items-center justify-center`} aria-hidden>
          <CheckCircle2 className="h-3.5 w-3.5 text-white" />
        </span>
      )}
    </button>
  );
};

export function WorkflowActionsPanel({ title = 'Workflow Actions', icon: Icon = Send, headerRight, cards, selected, onSelect, panels = {}, activeState, banner, footer, testId = 'workflow-actions-panel' }) {
  return (
    <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid={testId}>
      <div className="px-5 py-3.5 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon className="w-[18px] h-[18px] text-indigo-500" />
          <h3 className="text-[17px] font-bold text-slate-900 tracking-tight">{title}</h3>
        </div>
        {headerRight}
      </div>
      <div className="px-4 pb-4 space-y-3">
        {!activeState && banner}
        {activeState || (
          <>
            {cards.length > 0 && (
              <div className={`grid grid-cols-1 md:grid-cols-${Math.min(cards.length, 4)} gap-3`} role="radiogroup" data-testid={`${testId}-grid`}>
                {cards.map((c) => (
                  <ActionCard key={c.value} card={c} isSelected={selected === c.value} onSelect={(v) => onSelect(selected === v ? null : v)} />
                ))}
              </div>
            )}
            {selected && panels[selected] && (
              <div key={selected} className="wf-in relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4" data-testid={`${testId}-panel-${selected}`}>
                <div className={`wf-swoosh ${toneOf(selected).swoosh}`} aria-hidden />
                <div className="relative space-y-4">{panels[selected]}</div>
              </div>
            )}
          </>
        )}
        {footer}
      </div>
    </div>
  );
}

const NOTE_MAX = 1000;

/** Quick-chip reason picker + required "Add Update" — chips come from the admin-managed
 *  quick_reason_chips collection (`category`). Single-select: the chosen chip is the reason.
 *  MLS-PENDING-NOTES-001: a chip's template REPLACES the previous suggestion (never appends);
 *  manual edits are protected by a confirm; "Clear All" resets chips + text; a custom note
 *  without a chip is allowed (parent records it under the "Other" reason). */
export function ReasonChipsField({ category, reason, onReasonChange, note, onNoteChange, accent = 'amber', Icon = Zap, testId }) {
  const [chips, setChips] = useState([]);
  const lastAuto = useRef('');   // last template we put in the box — equality ⇒ not manually edited
  useEffect(() => {
    let live = true;
    fetch(`${API}/quick-chips?category=${category}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (live) setChips(Array.isArray(d) ? d : (d.chips || [])); })
      .catch(() => {});
    return () => { live = false; };
  }, [category]);

  const untouched = !note.trim() || note.trim() === lastAuto.current.trim();
  const setAuto = (text) => { lastAuto.current = text; onNoteChange(text); };
  const pick = (chip) => {
    if (reason === chip.label) {
      onReasonChange('');
      if (untouched) setAuto('');
      return;
    }
    onReasonChange(chip.label);
    const tpl = (chip.template || '').trim();
    if (!tpl) return;
    if (untouched) { setAuto(tpl); return; }
    if (window.confirm(`Replace your current update text with the suggested note for "${chip.label}"?`)) setAuto(tpl);
  };
  const clearAll = () => { onReasonChange(''); setAuto(''); };
  const hasAnything = !!reason || !!note.trim();
  const purple = accent === 'purple';
  const sel = purple ? 'border-purple-500 bg-purple-500 text-white shadow-sm' : 'border-orange-400 bg-orange-500 text-white shadow-sm';
  const tile = purple ? 'bg-purple-50 text-purple-600' : 'bg-orange-50 text-orange-500';
  const focus = purple ? 'focus:ring-purple-300 focus:border-purple-400' : 'focus:ring-orange-300 focus:border-orange-400';
  return (
    <>
      <div>
        <div className="flex items-start gap-3 mb-3">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tile}`}><Icon className="h-5 w-5" /></span>
          <div className="min-w-0">
            <div className="text-[15px] font-bold text-slate-900 leading-tight">Quick Reasons</div>
            <div className="text-[12.5px] text-slate-500">Select a reason or enter a custom update below.</div>
          </div>
          {hasAnything && (
            <button type="button" onClick={clearAll}
              className="ml-auto inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
              data-testid={`${testId}-clear-all`}>
              <X className="w-3.5 h-3.5" /> Clear All
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2.5" data-testid={`${testId}-chips`}>
          {chips.map((c) => {
            const on = reason === c.label;
            return (
              <button key={c.id || c.label} type="button" onClick={() => pick(c)}
                className={`wf-chip inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300
                  ${on ? sel : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}
                data-testid={`${testId}-chip-${(c.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} aria-pressed={on}>
                {c.label}{on && <CheckCircle2 className="w-3.5 h-3.5" />}
              </button>
            );
          })}
          {chips.length === 0 && <span className="text-xs text-slate-400">No quick reasons configured.</span>}
        </div>
      </div>
      <div>
        <label className="block text-[13px] font-semibold text-slate-800 mb-1.5">Add Update <span className="text-red-500">*</span></label>
        <textarea value={note} onChange={(e) => onNoteChange(e.target.value.slice(0, NOTE_MAX))} rows={3} maxLength={NOTE_MAX}
          placeholder="Describe the current status / what you are waiting for…"
          className={`w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl bg-white shadow-inner focus:outline-none focus:ring-2 ${focus} transition-[box-shadow,border-color] duration-150`}
          data-testid={`${testId}-note`} />
        <div className="flex items-start justify-between gap-3 mt-1">
          {!reason && !!note.trim() && /pending$/.test(category) ? (
            <p className="text-[11.5px] text-slate-500" data-testid={`${testId}-other-hint`}>No quick reason selected — this update will be recorded under “Other”.</p>
          ) : <span />}
          <span className="shrink-0 text-[11.5px] text-slate-400 tabular-nums">{note.length.toLocaleString()} / {NOTE_MAX.toLocaleString()}</span>
        </div>
      </div>
    </>
  );
}

/** Active-state card: white, accent bar, status pill, reason, latest update, who/when, actions; collapsible. */
export function ActiveStateCard({ tone = 'amber', label, Icon, reason, update, meta, actions, testId }) {
  const [open, setOpen] = useState(true);
  const t = {
    amber: { bar: 'border-l-orange-400', pill: 'bg-orange-50 text-orange-700', icon: 'bg-orange-50 text-orange-500', swoosh: 'is-orange' },
    purple: { bar: 'border-l-purple-500', pill: 'bg-purple-50 text-purple-700', icon: 'bg-purple-50 text-purple-600', swoosh: '' },
    blue: { bar: 'border-l-blue-500', pill: 'bg-blue-50 text-blue-700', icon: 'bg-blue-50 text-blue-600', swoosh: 'is-blue' },
  }[tone];
  return (
    <div className={`wf-in relative overflow-hidden rounded-2xl border border-slate-200 border-l-4 ${t.bar} bg-white shadow-sm px-4 py-3.5 flex items-start gap-3`} data-testid={testId}>
      <div className={`wf-swoosh ${t.swoosh}`} aria-hidden />
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="relative mt-3 shrink-0 rounded-md p-0.5 text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
        data-testid={`${testId}-toggle`}>
        <ChevronDown className={`wf-chevron w-4 h-4 ${open ? '' : '-rotate-90'}`} />
      </button>
      <span className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${t.icon}`}>
        {Icon ? <Icon className="h-6 w-6" /> : <CheckCircle2 className="h-6 w-6" />}
      </span>
      <div className="relative min-w-0 flex-1 space-y-1">
        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[13px] font-semibold ${t.pill}`}>{label}</span>
        {reason && <div className="text-[17px] font-bold text-slate-900 leading-snug truncate" data-testid={`${testId}-reason`}>{reason}</div>}
        {open && (
          <div className="wf-in space-y-1.5">
            {update && <div className="text-[14px] text-slate-700 whitespace-pre-wrap" data-testid={`${testId}-update`}>{update}</div>}
            {meta && <div className="flex items-center gap-1.5 text-[12.5px] text-slate-500" data-testid={`${testId}-meta`}><User className="w-3.5 h-3.5" />{meta}</div>}
          </div>
        )}
      </div>
      {actions && <div className="relative flex items-center gap-2 shrink-0 self-center">{actions}</div>}
    </div>
  );
}

export const SubmitRow = ({ label, onClick, disabled, className, testId }) => (
  <div className="flex justify-end">
    <Button type="button" disabled={disabled} className={`h-10 px-5 text-sm font-semibold rounded-xl shadow-sm transition-[background-color,transform] duration-150 active:scale-[0.98] ${className || ''}`} onClick={onClick} data-testid={testId}>
      <Send className="w-4 h-4 mr-2" /> {label}
    </Button>
  </div>
);

/* ---- Completion checklist ------------------------------------------------------------------- */
const ROW_ICON = [
  [/permit/i, ClipboardList], [/water/i, Droplet], [/sewer/i, Waves], [/trash/i, Trash2],
];
const splitBlocker = (text) => {
  const m = /^([^:(]+?)\s*([:(])\s*(.*)$/.exec(text || '');
  if (!m) return { label: text, desc: '' };
  let desc = m[3].trim();
  if (m[2] === '(') desc = desc.replace(/\)\s*$/, '');
  return { label: m[1].trim(), desc: desc.charAt(0).toUpperCase() + desc.slice(1) };
};
const iconFor = (label) => (ROW_ICON.find(([re]) => re.test(label)) || [null, AlertCircle])[1];

/** White checklist card: "Complete MLS Search · N requirements outstanding", collapsible rows. */
export function CompletionChecklist({ title = 'Complete MLS Search', blockers = [], testId = 'mls-complete-blockers' }) {
  const [open, setOpen] = useState(true);
  useEffect(() => { if (blockers.length) setOpen(true); }, [blockers.length]);
  const n = blockers.length;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="flex items-start gap-3 px-4 py-3.5">
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${n ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'}`}>
          {n ? <ClipboardList className="h-6 w-6" /> : <ClipboardCheck className="h-6 w-6" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-[17px] font-bold text-slate-900 tracking-tight">{title}</span>
            {n > 0 ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-0.5 text-[13px] font-semibold text-rose-600" data-testid={`${testId}-count`}>
                <AlertCircle className="h-3.5 w-3.5" /> {n} requirement{n === 1 ? '' : 's'} outstanding
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[13px] font-semibold text-emerald-700" data-testid={`${testId}-count`}>
                <CheckCircle className="h-3.5 w-3.5" /> All requirements met
              </span>
            )}
          </div>
          <div className="text-[13px] text-slate-500 mt-0.5">{n ? 'Resolve the following items to complete the MLS Search.' : 'Review the report, then complete the MLS Search.'}</div>
        </div>
        {n > 0 && (
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
            className="mt-2 shrink-0 rounded-md p-1 text-slate-500 hover:text-slate-800 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
            data-testid={`${testId}-toggle`}>
            <ChevronDown className={`wf-chevron w-5 h-5 ${open ? 'is-open' : ''}`} />
          </button>
        )}
      </div>
      {n > 0 && open && (
        <div className="wf-in mx-4 mb-4 rounded-xl border border-slate-100 bg-slate-50/60 divide-y divide-slate-200/70" data-testid={`${testId}-list`}>
          {blockers.map((b, i) => {
            const { label, desc } = splitBlocker(b);
            const RowIcon = iconFor(label);
            return (
              <div key={i} className="flex items-center gap-3 px-3.5 py-2.5 text-[14px]" data-testid="mls-complete-blocker-item">
                <Circle className="h-4 w-4 shrink-0 text-slate-300" />
                <RowIcon className="h-4 w-4 shrink-0 text-indigo-500" />
                <span className="font-semibold text-slate-800 whitespace-nowrap">{label}</span>
                {desc && <span className="text-slate-600 min-w-0">{desc}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
